/**
 * The editors — §5 of the plan.
 *
 * Rule 2: quantities are the only inline inputs; every other edit is one of
 * these. A popover on a desktop, a sheet on a phone, and the same component
 * either way — `<EditorShell>` is the only thing that knows which.
 *
 * The one with teeth is `<LineEditor>`. A price below standard is the single
 * edit on this screen that somebody will be asked about six months later, so
 * it is the single edit that will not complete without a reason. Everything
 * else here is a form.
 *
 * Step 8 moved the line, visit and service popovers into `builder/popovers/`
 * (`builder-engine-and-consistency.md` §5.1). The search and the package
 * group's editor stay here; the rest is forwarded for one release.
 */
import {
  Suspense, lazy, useEffect, useMemo, useRef, useState, type ComponentType,
} from 'react'
import { QUOTE_LINE_KIND_LABELS, type QuotePackageInstanceView } from '@rowted/shared'
import { cn } from '../lib/cn.js'
import { request } from '../lib/api.js'
import { Button, Segmented, Tag } from '../components/primitives.js'
import { DateField } from '../components/date-field.js'
import { FieldShell } from '../components/fields.js'
import { EditorActions, EditorHeader, EditorInput, EditorShell } from '../builder/editor-shell.js'
import { formatNumber, formatRate } from '../lib/format.js'
import { IconGrid, IconPlus, IconSearch, RECORD_ICONS } from '../frame/icons.js'
import { ADD_LINK_LABELS } from '../builder/rows/line-row.js'
import { KindTile } from '../builder/kind-tile.js'
import { useMe } from '../lib/use-me.js'
import {
  CREATE_AS_MATERIAL, PICK_PER_UNIT, PICK_RECIPE_ITEMS, PICK_SERVICE_AS_VISIT,
} from '../builder/copy.js'
import { DivisionTag } from '../builder/rows/service-row.js'
import { VisitTag } from '../builder/rows/visit-card.js'
export * from '../builder/popovers/line-popover.js'
export * from '../builder/popovers/visit-popover.js'
export * from '../builder/popovers/service-popover.js'
/* The names they had before step 8, for one release. */
export { LinePopover as LineEditor } from '../builder/popovers/line-popover.js'
export { VisitPopover as VisitEditor } from '../builder/popovers/visit-popover.js'
export { ServicePopover as ServiceEditor } from '../builder/popovers/service-popover.js'

/*
 * The box itself moved to `builder/editor-shell.tsx` so the package builder
 * draws the same one. Re-exported here because half the app already imports
 * it from this file, and a module that moves house leaves a forwarding
 * address.
 */
export { EditorShell } from '../builder/editor-shell.js'

// ---------------------------------------------------------------------------
// §5.1 — one search for everything
// ---------------------------------------------------------------------------

export interface Pickable {
  key: string
  kind: 'package' | 'service' | 'item' | 'labor' | 'deployment' | 'equipment'
  label: string
  detail: string
  /** "Owned" or "Rental" — which table an equipment row came out of. */
  tag?: string
  /** A service that the property has already been measured for. */
  measured?: number | null
  measurementId?: string | null
  /** What to POST. */
  body: Record<string, unknown>
  /**
   * cb-04 · 3: the price at the right of a line row, with what it is per
   * under it — `$1.26` / `per sq ft`. Absent on a package and a service.
   */
  price?: number
  per?: string
  /**
   * Item 3.4: a material's SKU, or an owned machine's asset tag — on the
   * title line beside the name, and what `q` matches as well as the name.
   */
  code?: string | null
  /**
   * Item 3.3: a catalog service's chips — its division, what it is measured
   * by, and how many lines its own recipe holds.
   */
  division?: { name: string; code: string } | null
  measureName?: string | null
  lineCount?: number
}

interface PickResponse {
  packages: { packageId: string; name: string; asks: string; qtyUnit: string
    divisionName: string | null; measureUnit: string | null; measureName: string | null
    visits: number; summary: string | null; measured: number | null
    measurementId: string | null; defaultQty: number; defaultMeasure: number | null }[]
  services: { serviceId: string; name: string; unit: string; divisionName: string | null
    divisionCode?: string | null
    measureUnit: string | null; measureName: string | null
    tiers: number; summary: string | null; measured: number | null
    measurementId: string | null
    /* Lines in the service's own recipe (`service_recipe_lines`), never a package's. */
    lineCount?: number }[]
  items: { itemId: string; name: string; unit: string; price: number; onHand: number | null
    /* cb-04 · 3's sub-line, `Hardscape · Ewing Supply`: the category and the preferred vendor. */
    categoryName?: string | null; vendorName?: string | null; sku?: string | null }[]
  labor: { laborRateId: string; name: string; rate: number; divisionName: string | null }[]
  deployment: { deploymentCostId: string; name: string; unit: string; price: number }[]
  equipment: { equipmentId: string | null; rentalEquipmentId: string | null; rental: boolean
    name: string; unit: string; price: number; assetTag?: string | null
    /** How many the fleet has, on an owned kind of machine (0129). */
    machines?: number | null }[]
}

/**
 * What goes inside a service. Services are not here.
 *
 * A service is the container; the five kinds below are what a container
 * holds, and offering both in one list invited somebody to add a service to
 * a service. "+ Add service" has its own search for that.
 *
 * `sub` is gone: a subcontractor is labor, and a sixth chip for a kind that
 * is the fifth one under another name taught nobody anything.
 */
/**
 * The chips: the kinds the catalog actually holds, in the product's words.
 *
 * Two separate things had to be true and only one was. The *words* used to be
 * their own list — "Items", "Deployment" — while every row and total on the
 * same screen said "Material" and "Fee"; those come from
 * `QUOTE_LINE_KIND_LABELS` now, so what you pick from reads like what you get.
 *
 * But the *set* is not every line kind. `sub` is a line somebody types onto a
 * quote; there is no subcontractor catalog behind it, so `/catalog/pick`
 * returns nothing for it and the chip was a dead end. The picker offers what
 * can be picked.
 */
const PICKABLE_KINDS = ['item', 'labor', 'equipment', 'deployment'] as const
const KINDS = ['all', ...PICKABLE_KINDS] as const
export type PickKind = (typeof KINDS)[number]

/*
 * Plural, as the add links are — cb-04 · 3 draws `All · Materials · Labor ·
 * Equipment · Fees`: a chip is a filter over many, like `+ Materials`, while a
 * row still names its one kind in the singular.
 */
const KIND_LABELS: Record<PickKind, string> = {
  all: 'All',
  ...ADD_LINK_LABELS,
}

/**
 * The chip's value, in the words `GET /catalog/pick` accepts.
 *
 * Its enum is `all | packages | services | items | labor | deployment |
 * equipment` — note **items**, plural, and note that `sub` and `rental` are
 * not in it at all. The chips are named for what a quoter reads on a line
 * ("Material"), and this is the only place the two vocabularies meet.
 *
 * Getting this wrong is silent: the route 400s and the panel simply says
 * "Nothing matches", which reads as an empty catalog rather than a bad
 * request. It is worth the map.
 */
const KIND_QUERY: Record<PickKind, string> = {
  all: 'all',
  item: 'items',
  labor: 'labor',
  equipment: 'equipment',
  deployment: 'deployment',
}

/**
 * ⌘/ — the primary way anything gets onto a quote.
 *
 * Services first and always, because in this model a service *is* a service: picking
 * one creates the service and expands it in a single call, which is the one
 * interaction that saves the most typing in the product. Everything else
 * appends a line to whichever service is open.
 *
 * ⏎ adds to the open service; ⇧⏎ makes a new service of it. Both are in the
 * footer, because a keyboard shortcut nobody is told about is a shortcut
 * nobody uses.
 */
export function AddSearch({
  open, quoteId, propertyId, anchor, hasOpenService, mode = 'line', startKind,
  onClose, onPick, layer, asNewVisit = false,
}: {
  open: boolean
  /**
   * The quote this is adding to, when there is one.
   *
   * Optional because the *catalog* uses this panel too: a service's recipe
   * picks the same materials, labor, equipment and fees out of the same
   * `/catalog/pick`, and Aaron asked for the same popup rather than one that
   * looks like it. Nothing is sent to the server with it — it only says the
   * results are stale when the quote changes — so a caller with no quote
   * simply has nothing to invalidate on.
   */
  quoteId?: string
  propertyId?: string | null
  anchor: HTMLElement | null
  /** Off in the catalog: there is no "make it a new service" there. */
  hasOpenService?: boolean
  /**
   * `service` searches the catalog's services and nothing else; `line`
   * searches everything a service is made of and never a service.
   *
   * Two searches rather than one list with a services heading in it, because
   * the two answer different questions and land in different places — one
   * adds a container to a visit, the other puts something in a container.
   */
  mode?: 'package' | 'service' | 'line'
  /** The chip to open on: "+ Deployment" should not open on "All". */
  startKind?: PickKind
  onClose: () => void
  onPick: (pick: Pickable, asNewService: boolean) => void
  /** Opened over the catalog's service pop-out — see `Popover`'s `layer`. */
  layer?: number
  /**
   * `service` mode opened from Add visit ▾ → From a service… (item 3.2): the
   * pick makes a visit of its own, and the title and the footer say so.
   */
  asNewVisit?: boolean
}) {
  const [query, setQuery] = useState('')
  const [kind, setKind] = useState<PickKind>(startKind ?? 'all')
  const [results, setResults] = useState<PickResponse | null>(null)
  const [cursor, setCursor] = useState(0)
  const box = useRef<HTMLInputElement>(null)
  /** The name a `Create “…” as a material` press is making, while its form is up. */
  const [creating, setCreating] = useState<string | null>(null)
  /** cb-04 · 3: offered under + Materials, once something is typed. */
  const offersCreate = mode === 'line' && kind === 'item' && query.trim().length > 0
  /*
   * Only to somebody who may make one — the route is `company_settings: edit`
   * (`POST /items`). A row that opened a form the server would refuse is a
   * control that looks on and is not.
   */
  const meState = useMe()
  const me = meState.status === 'ready' ? meState.me : null
  const canCreateMaterial = me?.permissions?.company_settings === 'edit'

  useEffect(() => {
    if (!open) return
    setQuery('')
    setCursor(0)
    setKind(startKind ?? 'all')
    box.current?.focus()
  }, [open, startKind])

  useEffect(() => {
    if (!open) return
    const timer = window.setTimeout(() => {
      const params = new URLSearchParams({
        kinds: mode === 'package' ? 'packages'
          : mode === 'service' ? 'services'
            : KIND_QUERY[kind],
        /*
         * Eight was the route's default and it is not enough to pick from:
         * eight materials out of four hundred is a list that only works if
         * you already know the name. Twenty-five fills the panel and scrolls.
         */
        limit: '25',
      })
      if (query.trim()) params.set('q', query.trim())
      if (propertyId) params.set('propertyId', propertyId)
      void request<PickResponse>(`/catalog/pick?${params}`)
        .then((body) => { setResults(body); setCursor(0) })
        .catch(() => setResults(null))
    }, 180)
    return () => window.clearTimeout(timer)
  }, [open, query, kind, mode, propertyId, quoteId])

  const rows = useMemo<Pickable[]>(() => {
    if (!results) return []

    /*
     * A package is its own thing and its own search.
     *
     * It used to be findable only as a *service* carrying the `isPackage`
     * flag, which meant something else entirely: "this service appears
     * somewhere in some package's recipe". Picking one of those called
     * `POST /visits/:id/services` and put every line on a single visit —
     * which is what Aaron saw, and it was right, because that call was never
     * adding a package at all. A package with three trips now goes through
     * the endpoint that makes three visits.
     */
    if (mode === 'package') {
      return results.packages.map((row) => ({
        key: `package:${row.packageId}`,
        kind: 'package' as const,
        label: row.name,
        /*
         * The trips first, named and in order — "Drop off → Weekly swap →
         * Pick up" — because that is the thing a package *is* and the thing
         * somebody is choosing between. Then what it will ask for.
         */
        detail: [
          row.summary,
          row.asks === 'measurement' || row.asks === 'both'
            ? `by ${(row.measureName ?? 'measurement').toLowerCase()}`
            : null,
          row.asks === 'quantity' || row.asks === 'both' ? `per ${row.qtyUnit}` : null,
          row.divisionName,
        ].filter(Boolean).join(' · '),
        tag: `${row.visits} ${row.visits === 1 ? 'visit' : 'visits'}`,
        measured: row.measured,
        measurementId: row.measurementId,
        /*
         * What it lands at: the property's own measurement where there is
         * one, the package's default where there is not. Adjusting it is the
         * group card's quantity box, which is also what re-scales it later —
         * so there is one control for the number rather than a form on the
         * way in and a different box afterwards.
         */
        body: {
          packageId: row.packageId,
          qty: row.defaultQty,
          measure: row.measured ?? row.defaultMeasure ?? undefined,
          measurementId: row.measured ? row.measurementId : null,
        },
      }))
    }

    if (mode === 'service') {
      return results.services.map((row) => ({
        key: `service:${row.serviceId}`,
        kind: 'service' as const,
        label: row.name,
        /*
         * What it is made of, after the chips. What it is measured by used
         * to lead this line as "by turf area"; it is a chip of its own now
         * (item 3.3), because it is the number that will be filled in from the
         * property and saying so before the pick is what stops somebody
         * quoting an overseed off a wall measurement. "per each" went with
         * it: what a service is sold by is retired (0077) — its lines carry
         * the units.
         */
        detail: [row.tiers > 1 ? `${row.tiers} bands` : null, row.summary]
          .filter(Boolean).join(' · '),
        division: row.divisionName
          ? { name: row.divisionName, code: row.divisionCode ?? '' }
          : null,
        measureName: row.measureName,
        lineCount: row.lineCount,
        measured: row.measured,
        measurementId: row.measurementId,
        body: {
          serviceId: row.serviceId,
          ...(row.measured ? { measure: row.measured, measurementId: row.measurementId } : {}),
        },
      }))
    }

    return [
      /*
       * cb-04 · 3: what it is under the name — `Category · Vendor` — and what
       * it costs at the right, `$1.26` over `per sq ft`. The price left the
       * sub-line for its own column, so a list of materials reads down one
       * edge.
       */
      ...results.items.map((row) => ({
        key: `item:${row.itemId}`,
        kind: 'item' as const,
        label: row.name,
        detail: [row.categoryName, row.vendorName].filter(Boolean).join(' · '),
        code: row.sku ?? null,
        price: row.price,
        per: PICK_PER_UNIT(row.unit),
        body: { itemId: row.itemId },
      })),
      ...results.labor.map((row) => ({
        key: `labor:${row.laborRateId}`,
        kind: 'labor' as const,
        label: row.name,
        detail: row.divisionName ?? '',
        price: row.rate,
        per: PICK_PER_UNIT('h'),
        body: { laborRateId: row.laborRateId },
      })),
      ...results.equipment.map((row) => ({
        // Owned or hired in — one heading, and the tag says which, because one
        // goes on the morning board and the other on a hire. An owned row is a
        // kind of machine (0129): "3 in the fleet", never one of them.
        key: `eq:${row.rental ? 'r' : 'o'}:${row.rentalEquipmentId ?? row.equipmentId}`,
        kind: 'equipment' as const,
        label: row.name,
        detail: !row.rental && row.machines ? `${row.machines} in the fleet` : '',
        code: null,
        price: row.price,
        per: PICK_PER_UNIT(row.unit),
        tag: row.rental ? 'Rental' : 'Owned',
        body: row.rentalEquipmentId
          ? { rentalEquipmentId: row.rentalEquipmentId }
          : { equipmentId: row.equipmentId },
      })),
      ...results.deployment.map((row) => ({
        key: `dep:${row.deploymentCostId}`,
        kind: 'deployment' as const,
        label: row.name,
        detail: '',
        price: row.price,
        per: PICK_PER_UNIT(row.unit),
        body: { deploymentCostId: row.deploymentCostId },
      })),
    ]
  }, [results, mode])

  if (!open) return null

  const sections: { label: string; kind: Pickable['kind'] }[] = mode === 'package'
    ? [{ label: 'Packages · the visits come with it', kind: 'package' }]
    : mode === 'service'
      ? [{ label: 'Services · a whole one, with its recipe', kind: 'service' }]
      : PICKABLE_KINDS.map((one) => ({
        label: one === 'equipment'
          ? 'Equipment · owned and hired in'
          : QUOTE_LINE_KIND_LABELS[one],
        kind: one as Pickable['kind'],
      }))

  return (
    <EditorShell
      open
      onClose={onClose}
      anchor={anchor}
      label={mode === 'package'
        ? 'Add a package'
        : mode === 'service'
          ? (asNewVisit ? PICK_SERVICE_AS_VISIT.label : 'Add a service')
          : 'Add to this service'}

      /*
       * A ceiling, so a picker on a tall screen is still a picker: the search
       * box has to stay in sight of the row it is about, and a list 900px
       * long is a list whose bottom is nowhere near either.
       */
      maxHeight={460}
      fullScreen
      fill
      layer={layer}
      footer={(
        <span className="text-[11px] text-text-faint">
          {mode === 'package'
            ? '↑↓ move · ⏎ add it, with its visits'
            : mode === 'service'
              ? (asNewVisit ? PICK_SERVICE_AS_VISIT.footer : '↑↓ move · ⏎ add it to the visit')
              : !quoteId
                /* The catalog's recipe: there is no service to make, only a line to add (cb-04 · 3). */
                ? '↑↓ move · ⏎ add · esc close'
                : (
                  <>
                    ↑↓ move · ⏎ {hasOpenService ? 'add to the open service' : 'add as a new service'}
                    {hasOpenService && ' · ⇧⏎ add as a new service'}
                  </>
                )}
        </span>
      )}
    >
      {/*
        * One scroller, and it is the list. The box and the chips are how you
        * change what is in the list, so they hold their ground while it moves
        * — and the shell is in `fill` mode so it does not wrap the lot in a
        * second scrollbar.
        */}
      <div className="flex min-h-0 flex-1 flex-col gap-2.5">
        <div className={cn('flex shrink-0 items-center gap-2 rounded-field border border-line',
          'bg-field px-2',
          'focus-within:border-accent-line focus-within:ring-2 focus-within:ring-accent-ring')}>
          <IconSearch size={15} />
          <input
            ref={box}
            value={query}
            placeholder={mode === 'package'
              ? 'Search your packages…'
              : mode === 'service'
                ? 'Search your services…'
                // A material is found by its SKU too, so the box says so (Aaron, step 23).
                : 'Add an item, labor, equipment — or search a SKU…'}
            onChange={(event) => setQuery(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'ArrowDown') {
                event.preventDefault(); setCursor((at) => Math.min(at + 1, rows.length - 1))
              }
              if (event.key === 'ArrowUp') {
                event.preventDefault(); setCursor((at) => Math.max(at - 1, 0))
              }
              if (event.key === 'Enter' && rows[cursor]) {
                event.preventDefault()
                onPick(rows[cursor]!, event.shiftKey || !hasOpenService)
              }
              if (event.key === 'Enter' && !rows[cursor] && offersCreate && canCreateMaterial) {
                event.preventDefault()
                setCreating(query.trim())
              }
              if (event.key === 'Escape') onClose()
            }}
            /*
              * The ring belongs to the box, not to the input inside it: the
              * global focus style drew a rounded rectangle around the text
              * area alone, floating inside a search box it did not line up
              * with. `focus-visible:shadow-none` is what turns that off.
              */
            className={cn('h-9 min-w-0 flex-1 bg-transparent text-body text-text',
              'focus:outline-none focus-visible:shadow-none')}
          />
        </div>

        {/* The kind chips are for lines. A package and a service have one list. */}
        <div className={cn('flex shrink-0 gap-1.5', mode !== 'line' && 'hidden')}>
          {KINDS.map((value) => (
            <button
              key={value}
              type="button"
              onClick={() => setKind(value)}
              className={cn(
                'h-7 rounded-chip px-2.5 text-[11.5px] font-semibold',
                value === kind
                  ? 'bg-text text-surface'
                  : 'border border-line bg-surface text-text-body',
              )}
            >
              {KIND_LABELS[value]}
            </button>
          ))}
        </div>

        <div className="-mx-1 min-h-0 flex-1 overflow-y-auto">
          {rows.length === 0 && (
            <p className="px-1 py-3 text-body text-text-faint">
              Nothing matches. Try fewer words.
            </p>
          )}

          {sections.map((section) => {
            const mine = rows.filter((row) => row.kind === section.kind)
            if (mine.length === 0) return null
            return (
              <div key={section.kind}>
                <p className="m-0 px-1 pb-1 pt-2 text-[10.5px] font-semibold uppercase tracking-[0.06em] text-text-faint">
                  {section.label}
                </p>
                {mine.map((row) => {
                  const index = rows.indexOf(row)
                  return (
                    <button
                      key={row.key}
                      type="button"
                      onMouseEnter={() => setCursor(index)}
                      onClick={() => onPick(row, !hasOpenService)}
                      className={cn(
                        'flex w-full items-center rounded-field px-1.5 text-left',
                        /* cb-04 · 3: a 50px row, 12 between the tile, the name and the price. */
                        mode === 'line' ? 'min-h-[50px] gap-3 py-1.5' : 'gap-2.5 py-2',
                        index === cursor && 'bg-accent-soft',
                      )}
                    >
                      {mode === 'line' && <PickTile kind={row.kind} />}
                      <span className={cn('flex min-w-0 flex-1 flex-col',
                        mode === 'service' ? 'gap-[3px]' : 'gap-px')}>
                        <b className="flex items-center gap-1.5 text-body font-semibold text-text">
                          <span className="min-w-0 truncate">{row.label}</span>
                          {/*
                            * The SKU — or an owned machine's asset tag — on
                            * the title line beside the name (item 3.4), so a
                            * code somebody typed into the box is visibly the
                            * reason the row matched.
                            */}
                          {row.code && (
                            <span data-pick-code="" title={row.code}
                              className="tnum max-w-[8rem] shrink-0 truncate text-[11.5px] font-medium text-text-muted">
                              {row.code}
                            </span>
                          )}
                          {/*
                            * What kind of line this will become, in its own
                            * colour. Aaron: "since the add line popup has an
                            * all filter that consolidates materials,
                            * equipment, labor and fees, it would be nice if we
                            * put a colored chip beside each item indicating
                            * which one they are."
                            *
                            * Under every chip, not only All (item 3.4): the
                            * row should read the same whichever filter found
                            * it. The colours are the `--status-quoteLine-*`
                            * pairs the line's own tile will wear once it is
                            * on the quote, so the chip is a promise about
                            * what the row turns into.
                            */}
                          {mode === 'line' && <KindChip kind={row.kind} />}
                          {row.tag && <Tag>{row.tag}</Tag>}
                        </b>
                        {mode === 'service'
                          ? <ServicePickLine row={row} />
                          : row.detail && (
                            <span className={cn('block truncate text-[11.5px]',
                              mode === 'line' ? 'text-text-muted' : 'text-text-faint')}>
                              {row.detail}
                            </span>
                          )}
                      </span>
                      {row.price !== undefined && (
                        <span className="tnum flex shrink-0 flex-col items-end gap-px">
                          <b className="text-[13.5px] font-bold text-text">{formatRate(row.price)}</b>
                          {row.per && <span className="text-[11px] text-text-faint">{row.per}</span>}
                        </span>
                      )}
                      {/*
                        * The saving this search exists for: a measure that was
                        * walked on site, offered against the service it fits, so
                        * nobody retypes it off a clipboard.
                        */}
                      {row.measured ? (
                        <span className="shrink-0 whitespace-nowrap text-[11.5px] font-semibold text-accent">
                          {formatNumber(row.measured)} measured
                        </span>
                      ) : null}
                    </button>
                  )
                })}
              </div>
            )
          })}
        </div>

        {/*
          * cb-04 · 3: the last row, full width, 44 tall, on the accent tint
          * with its rule above — `Create “pav” as a material`. It opens the
          * material form itself, stacked over this picker with the typed name
          * in it, and the material it saves is the one picked.
          */}
        {offersCreate && canCreateMaterial && (
          <button
            type="button"
            data-create-material=""
            onClick={() => setCreating(query.trim())}
            className={cn('-mx-4 -mb-4 flex h-11 shrink-0 items-center gap-2.5 border-t border-accent-line',
              'bg-accent-soft px-3.5 text-left text-[13.5px] text-accent-dark')}
          >
            <span aria-hidden className="flex h-5 w-5 shrink-0 items-center justify-center rounded-[6px] bg-accent text-surface">
              <IconPlus size={13} />
            </span>
            <span className="min-w-0 truncate">
              {CREATE_AS_MATERIAL.create} <b>“{query.trim()}”</b> {CREATE_AS_MATERIAL.asA}
            </span>
          </button>
        )}
      </div>

      {creating !== null && me && (
        <Suspense fallback={null}>
          <MaterialFormLazy
            itemId={null}
            canEdit
            available={me.scope?.available ?? []}
            initialName={creating}
            layer={MATERIAL_LAYER}
            onClose={() => setCreating(null)}
            onSaved={({ id }) => {
              const name = creating
              setCreating(null)
              if (!id) return
              onPick({
                key: `item:${id}`, kind: 'item', label: name, detail: '', body: { itemId: id },
              }, !hasOpenService)
            }}
          />
        </Suspense>
      )}
    </EditorShell>
  )
}

/**
 * The material form, loaded the first time somebody presses Create — the
 * same lazy split `RecordLookup` makes for its stacked forms. It opens with
 * the typed words in Name, over this picker (`layer`).
 */
const MaterialFormLazy = lazy(() => import('../components/forms/material-form.js')
  .then((module) => ({ default: module.MaterialForm })))

/** `RecordLookup`'s `STACKED`: a form opened from a picker sits over it. */
const MATERIAL_LAYER = 70

/**
 * cb-04 · 3's kind tile: 28 square at radius 8 with a 16px glyph — the line's
 * own tile and colour (`KindTile`), a size up.
 */
const TILE_GLYPHS = Object.fromEntries(PICKABLE_KINDS.map((kind) => {
  const Icon = RECORD_ICONS[kind]
  function Glyph(props: { size?: number; strokeWidth?: number }) {
    return <Icon {...props} size={16} />
  }
  return [kind, Glyph]
})) as Record<(typeof PICKABLE_KINDS)[number], ComponentType<{ size?: number; strokeWidth?: number }>>

function PickTile({ kind }: { kind: Pickable['kind'] }) {
  if (kind === 'package' || kind === 'service') return null
  return <KindTile kind={kind} size={28} className="!rounded-[8px]" icon={TILE_GLYPHS[kind]} />
}

/**
 * A catalog service's second line (Aaron, item 3.3): its division, what it is
 * measured by and how many lines its own recipe holds — then what the recipe
 * is made of, which is the part that truncates.
 *
 * The tags are the ones the service wears once it is on the quote: the
 * division is the rung's own `DivisionTag`, coloured from the code; the others
 * are the visit card's plain `VisitTag`. Under the name rather than beside it,
 * as the rung puts them (`ServiceShell`'s `sub`), so a long name keeps its
 * words.
 */
function ServicePickLine({ row }: { row: Pickable }) {
  if (!row.division && !row.measureName && row.lineCount === undefined && !row.detail) return null
  return (
    <span data-service-chips="" className="flex min-w-0 items-center gap-[7px]">
      {row.division && <DivisionTag name={row.division.name} code={row.division.code} />}
      {row.measureName && (
        <VisitTag>
          <span aria-hidden className="mr-1 inline-flex"><IconGrid size={11} /></span>
          {row.measureName}
        </VisitTag>
      )}
      {row.lineCount !== undefined && <VisitTag>{PICK_RECIPE_ITEMS(row.lineCount)}</VisitTag>}
      {row.detail && (
        <span className="min-w-0 truncate text-[11.5px] text-text-faint">{row.detail}</span>
      )}
    </span>
  )
}

/** The Segmented import is used by the billing tab; re-exported so it stays one import. */
export { Segmented }

// ---------------------------------------------------------------------------
// The package group's own editor
// ---------------------------------------------------------------------------

/**
 * The three numbers a package group scales from, in one box.
 *
 * `docs/catalog-and-packages.md` §5.2. The quantity and the measure are on the
 * group header as boxes because they are what people change; this is where the
 * start date lives, and where somebody goes to stop the group following the
 * package at all.
 *
 * Detach is in here rather than only on the header because it is the decision
 * that cannot be undone by typing: after it, the visits are ordinary visits
 * and changing the package will never reach them again. It says so.
 */
export function PackageEditor({
  instance, anchor, onClose, onSave, onDetach, onResetMeasure, onClearMeasure,
}: {
  instance: QuotePackageInstanceView
  anchor: HTMLElement | null
  onClose: () => void
  onSave: (patch: { qty?: number; measure?: number; startsOn?: string }) => Promise<boolean>
  onDetach: () => void
  /** The service editor's pair, for the one number every visit here follows. */
  onResetMeasure: () => Promise<boolean>
  onClearMeasure: () => Promise<boolean>
}) {
  const [qty, setQty] = useState(instance.qty === null ? '' : String(instance.qty))
  const [measure, setMeasure] = useState(
    instance.measure === null ? '' : String(instance.measure))
  const [startsOn, setStartsOn] = useState(instance.startsOn)
  const [busy, setBusy] = useState(false)

  /** Reset and Clear save at once, like the service editor's. */
  const act = async (run: () => Promise<boolean>, next: number | null) => {
    setBusy(true)
    if (await run()) setMeasure(next === null ? '' : String(next))
    setBusy(false)
  }

  const done = async () => {
    setBusy(true)
    const patch: { qty?: number; measure?: number; startsOn?: string } = {}
    if (instance.asksQty && Number.isFinite(Number(qty)) && Number(qty) > 0) {
      patch.qty = Number(qty)
    }
    if (instance.asksMeasure && measure.trim() !== '' && Number.isFinite(Number(measure))
      && Number(measure) !== instance.measure) {
      patch.measure = Number(measure)
    }
    if (startsOn !== instance.startsOn) patch.startsOn = startsOn
    const saved = await onSave(patch)
    setBusy(false)
    if (saved) onClose()
  }

  return (
    <EditorShell
      open
      onClose={onClose}
      anchor={anchor}
      label={instance.name}

      header={(
        <EditorHeader
          kind="package"
          name={instance.name}
          facts={`${instance.visitIds.length} `
            + `visit${instance.visitIds.length === 1 ? '' : 's'} follow these numbers`}
        />
      )}
      footer={(
        <EditorActions
          fact={(
            <>
              Package total <b className="tnum text-text">{formatRate(instance.total)}</b>
            </>
          )}
          onRemove={() => { onDetach(); onClose() }}
          removeLabel="Detach"
          onDone={() => void done()}
          busy={busy}
        />
      )}
    >
      <div className="flex flex-col gap-3.5">
        <div className="grid grid-cols-2 gap-3">
          {/* Only where the package is sold by a count — the bar's own rule (step 22). */}
          {instance.asksQty && (
            <FieldShell label="How many" hint={instance.qtyUnit}>
              <EditorInput
                autoFocus
                tone="number"
                value={qty}
                aria-label="How many"
                onChange={(event) => setQty(event.target.value)}
                inputMode="decimal"
              />
            </FieldShell>
          )}
          {instance.asksMeasure && (
            <FieldShell label="Measure" hint={instance.measureUnit ?? undefined}>
              <EditorInput
                tone="number"
                value={measure}
                aria-label="Measure"
                placeholder="Not set"
                onChange={(event) => setMeasure(event.target.value)}
                inputMode="decimal"
              />
            </FieldShell>
          )}
          <FieldShell
            label="Starts"
            hint="Every visit in the package is timed from this date."
          >
            {/* `DateField`, not `<input type="date">` — §1 of the remediation,
                and the only date control that draws the same on every OS. */}
            <DateField label="Starts on" value={startsOn} onChange={setStartsOn}
                  size="field" display="long" />
          </FieldShell>
        </div>

        {instance.asksMeasure && (
          <div className="flex gap-2">
            <Button
              size="toolbar"
              disabled={busy || instance.propertyMeasure === null}
              title={instance.propertyMeasure === null
                ? `The property has no ${
                  (instance.measurementName ?? 'measurement').toLowerCase()} yet`
                : `Back to the property's ${formatNumber(instance.propertyMeasure)} ${
                  instance.measureUnit ?? ''}, and every line back to the package`}
              onClick={() => void act(onResetMeasure, instance.propertyMeasure)}
            >
              Reset to the property
            </Button>
            <Button
              size="toolbar"
              disabled={busy || (instance.measure === null && measure.trim() === '')}
              title="No measure: every line stays, at what it needs at any size"
              onClick={() => void act(onClearMeasure, null)}
            >
              Clear measure
            </Button>
          </div>
        )}

        <p className="m-0 text-[12px] leading-[1.45] text-text-muted">
          A line somebody has typed over keeps its own number — Reset puts it
          back too — and a visit somebody deleted stays deleted.
        </p>
      </div>
    </EditorShell>
  )
}

/**
 * What kind of line a picker row will become — the `--status-quoteLine-*`
 * pair, at the size of a tag rather than a status chip.
 *
 * `StatusChip` is the same colours with a dot and a 22px body, which is right
 * in a table cell and too loud beside a 13px name in a list of forty. This is
 * that chip's quieter cousin and reads from the same two tokens, so a
 * material is the same blue here, on the row it becomes, and on that row's
 * tile.
 */
function KindChip({ kind }: { kind: Pickable['kind'] }) {
  const label = (QUOTE_LINE_KIND_LABELS as Record<string, string>)[kind] ?? kind
  return (
    <span
      className={cn('inline-flex h-[18px] shrink-0 items-center whitespace-nowrap',
        'rounded-tag px-1.5 text-tag font-semibold')}
      style={{
        color: `rgb(var(--status-quoteLine-${kind}-fg))`,
        background: `rgb(var(--status-quoteLine-${kind}-bg))`,
      }}
    >
      {label}
    </span>
  )
}
